import React, { useMemo, useState } from 'react';
import type { VcsConnection } from './vcs-connection-modal-picker';
import { FormattedMessage } from 'react-intl';
import { Row } from 'components/common/grid';
import { VcsConnectionModalFormAuthenticationTypeButton } from './vcs-connection-modal-form-authentication-type-button';
import { VcsConnectionModalButtons } from './vcs-connection-modal-buttons';
import { VcsConnectionModalFormOauth } from './vcs-connection-modal-form-oauth';
import { VcsConnectionModalFormAccessToken } from './vcs-connection-modal-form-access-token';
import { VcsConnectionModalFormVcsAgent } from './vcs-connection-modal-form-vcs-agent';
import styled from '@emotion/styled';
import type { AuthenticationType, VcsConnectionsForm } from './access-card/use-connection-modal-access-card-form.hook';
import { useVcsConnectionModalForm } from './access-card/use-connection-modal-access-card-form.hook';
import { getAvailableAuthenticationTypes } from 'components/organizations/vcs/vcs-connection-modal/access-card/supported-vcs-connections-common';

function getModalForm(authenticationType: AuthenticationType): React.FunctionComponent<VcsConnectionsForm> {
  switch (authenticationType) {
    case 'OAuth':
      return VcsConnectionModalFormOauth;
    case 'AccessToken':
      return VcsConnectionModalFormAccessToken;
    case 'VcsAgent':
      return VcsConnectionModalFormVcsAgent;
  }
}

export const VcsConnectionsModalConnectionDetails: React.FC<{
  vcsConnection: VcsConnection;
  onSubmit: (data: any) => void;
  hideModal: () => void;
}> = ({ vcsConnection, onSubmit, hideModal }) => {
  const { form } = useVcsConnectionModalForm({ vcsConnection, onSubmit });
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const authenticationOptions = useMemo(() => {
    const authenticationTypes = getAvailableAuthenticationTypes(vcsConnection);
    return authenticationTypes.map((type: AuthenticationType) => ({
      label: (
        <span data-e2e={`configure-authentication-type-${type}-picker`}>
          <FormattedMessage id={`organization.settings.agents.vcs-connections-modal.authentication.${type}`} />
        </span>
      ),
      value: type,
      disabled: authenticationTypes.length === 1
    }));
  }, [vcsConnection]);

  const authenticationType = form.watch('authenticationType');

  const ModalForm = useMemo(() => getModalForm(authenticationType), [authenticationType]);

  return (
    <>
      <Row style={{ marginTop: '-8px' }}>
        <VcsConnectionModalFormAuthenticationTypeButton options={authenticationOptions} form={form} />
      </Row>
      <FormContainer>
        <ModalForm vcsConnection={vcsConnection} form={form} onTokenProcessingChange={setIsProcessingToken} />
      </FormContainer>
      <VcsConnectionModalButtons
        form={form}
        vcsConnection={vcsConnection}
        onSave={hideModal}
        isPending={isProcessingToken}
      />
    </>
  );
};

const FormContainer = styled.div`
  padding: 20px 0;
`;
